// external
import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CircularProgress } from '@mui/material';

// internal
import '../login/login.page.css';
import './reset.page.css';
import whiteLogo from '../../assets/logo-white.png';
import useService from '../../hooks/service.hook';
import AuthenticationService from '../../services/authentication.service';
import { FieldErrors, useFieldErrors } from '../../components/form-validation';

const ResetPage: FC = () => {
  // state
  type ResetField = 'password' | 'confirmPassword';

  const [loading, setLoading] = useState<boolean>(true);
  const [contentLoading, setContentLoading] = useState<boolean>(false);
  const { fieldErrors, setFieldErrors, clearFieldError } = useFieldErrors<ResetField>();
  const [passwordForm, setPasswordForm] = useState<any>({
    password: '',
    confirmPassword: '',
  });
  const [success, setSuccess] = useState<boolean>(false);

  // hooks
  const { token } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbarHelper();

  // services
  const authenticationService = useService(AuthenticationService);

  // component did mount
  useEffect(() => {
    authenticationService
      .validateResetPasswordLink(token as string)
      .then(() => setLoading(false))
      .catch((error) => {
        const message =
          error?.response?.status === 500
            ? 'There was an error while validating your reset password link. Please try again later'
            : 'Error: page does not exist';
        showSnackbar(message, 'error');
        navigate('/login');
      });
  }, []);

  const handleResetPassword = () => {
    setFieldErrors({});
    if (validate()) {
      setContentLoading(true);
      authenticationService
        .resetPasswordFromLink(token as string, passwordForm.password)
        .then(() => {
          setContentLoading(false);
          setSuccess(true);
          showSnackbar('Successfully reset password', 'success');
        })
        .catch(() => {
          setContentLoading(false);
          showSnackbar('There was an error while resetting your password. Please try again later', 'error');
        });
    }
  };

  const validate = (): boolean => {
    const errors: FieldErrors<ResetField> = {};
    if (passwordForm.password.length < 1) {
      errors.password = 'Password is required';
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getResetContent = () => {
    if (success) {
      return (
        <>
          <div className="reset-page-form-content-header">
            <CheckCircleOutlineIcon style={{ width: 70, height: 70 }} />
            <p className="title">Successfully Reset Password</p>
            <p className="description">
              You have successfully reset your password. Please log back in with your updated credentials to access your
              account.
            </p>
          </div>
          <Button
            variant="contained"
            style={{ textTransform: 'none', marginTop: 10 }}
            color="primary"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </>
      );
    } else {
      return (
        <>
          <div className="reset-page-form-content-header">
            <LockResetIcon style={{ width: 70, height: 70 }} />
            <p className="title">Reset Password</p>
            <p className="description">
              Type in your new password to update it in our systems. After updating you can then navigate to the home
              page to log back in
            </p>
          </div>
          <TextField
            id="reset-field-password"
            type="password"
            value={passwordForm.password}
            onChange={(evt: any) => {
              setPasswordForm({ ...passwordForm, password: evt.target.value });
              clearFieldError('password');
            }}
            label="New Password"
            variant="outlined"
            size="small"
            error={!!fieldErrors.password}
            helperText={fieldErrors.password || ''}
            style={{ marginBottom: 10 }}
          />
          <TextField
            id="reset-field-confirmPassword"
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(evt: any) => {
              setPasswordForm({ ...passwordForm, confirmPassword: evt.target.value });
              clearFieldError('confirmPassword');
            }}
            label="Confirm Password"
            variant="outlined"
            size="small"
            error={!!fieldErrors.confirmPassword}
            helperText={fieldErrors.confirmPassword || ''}
            style={{ marginBottom: 10 }}
          />
          <Button
            variant="contained"
            style={{ textTransform: 'none', marginTop: 10, marginBottom: 10 }}
            onClick={handleResetPassword}
          >
            Reset Password
          </Button>
          <hr />
          <Button
            variant="contained"
            style={{ textTransform: 'none', marginTop: 10 }}
            color="success"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </>
      );
    }
  };

  return (
    <>
      {loading ? (
        <div className="reset-page-loading-container">
          <CircularProgress style={{ height: 150, width: 150 }} />
        </div>
      ) : (
        <>
          <div className="login-header">
            <img src={whiteLogo} className="login-header-logo" />
          </div>
          <div className="login-body">
            <form className="login-form" style={{ minWidth: 350 }}>
              {contentLoading ? (
                <div className="reset-page-circle-progress-container">
                  <CircularProgress style={{ width: 100, height: 100 }} />
                </div>
              ) : (
                getResetContent()
              )}
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default ResetPage;
